import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '@/components/base/Animations';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useMessages } from '@/hooks/useMessages';
import { useAuth } from '@/context/AuthContext';
import type { ChatConversation, ChatMessage } from '@/services/api';

export default function MessagesPage() {
  const { user } = useAuth();
  const { conversations, loadConversations, loadMessages, markConversationRead, sendMessage } = useMessages();
  const [selectedConv, setSelectedConv] = useState<ChatConversation | null>(null);
  const [convMessages, setConvMessages] = useState<ChatMessage[]>([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadConversations();
  }, [user, loadConversations]);

  const openConversation = async (conv: ChatConversation) => {
    setSelectedConv(conv);
    const msgs = await loadMessages(conv.peerId);
    setConvMessages(msgs);
    await markConversationRead(conv.peerId);
    loadConversations();
  };

  const handleReply = async () => {
    const trimmed = replyText.trim();
    if (!trimmed || !selectedConv || !user) return;
    setSending(true);
    await sendMessage(selectedConv.peerId, {
      listingId: selectedConv.listingId || undefined,
      listingType: selectedConv.listingType || undefined,
      listingTitle: selectedConv.listingTitle || undefined,
      receiverId: selectedConv.peerId,
      receiverName: selectedConv.counterparty,
      content: trimmed,
    });
    setReplyText('');
    setSending(false);
    const msgs = await loadMessages(selectedConv.peerId);
    setConvMessages(msgs);
    loadConversations();
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return '刚刚';
    if (diffMin < 60) return `${diffMin}分钟前`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}小时前`;
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 7) return `${diffDay}天前`;
    return `${d.getMonth() + 1}月${d.getDate()}日 ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <>
      <Navbar />
      <div className="w-full bg-background-50 min-h-screen">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">站内信</h1>
              {totalUnread > 0 && (
                <span className="px-2.5 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                  {totalUnread} 条未读
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Conversation list */}
            <div className="lg:col-span-1">
              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-background-200">
                  <p className="text-foreground-600 text-xs font-medium">全部对话</p>
                </div>
                {conversations.length === 0 ? (
                  <div className="px-4 py-12 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                      <i className="ri-chat-3-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500 text-sm mt-3">暂无对话</p>
                    <p className="text-foreground-400 text-xs mt-1">浏览供给或需求，向对方发送站内信开始对话</p>
                  </div>
                ) : (
                  <div className="divide-y divide-background-100 max-h-[600px] overflow-y-auto">
                    {conversations.map((conv) => (
                      <button
                        key={conv.peerId}
                        onClick={() => openConversation(conv)}
                        className={`w-full text-left px-4 py-3.5 transition-colors cursor-pointer hover:bg-background-100 ${
                          selectedConv && selectedConv.peerId === conv.peerId
                            ? 'bg-primary-50 border-l-2 border-primary-500'
                            : 'border-l-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-foreground-900 text-sm font-medium truncate">
                              {conv.counterparty}
                            </p>
                            <p className="text-foreground-400 text-xs mt-0.5 truncate">
                              {conv.lastMessage}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span className="text-foreground-400 text-xs">{formatTime(conv.lastMessageTime)}</span>
                            {conv.unreadCount > 0 && (
                              <span className="w-5 h-5 rounded-full bg-accent-500 text-background-50 text-xs flex items-center justify-center">
                                {conv.unreadCount}
                              </span>
                            )}
                          </div>
                        </div>
                        {conv.listingTitle && (
                          <p className="text-foreground-300 text-xs mt-1 truncate">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${
                              conv.listingType === 'supply' ? 'bg-primary-500' : 'bg-accent-500'
                            }`}></span>
                            {conv.listingTitle}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Conversation detail */}
            <div className="lg:col-span-2">
              {!selectedConv ? (
                <div className="bg-background-50 border border-background-200 rounded-lg flex items-center justify-center min-h-[500px]">
                  <div className="text-center px-4">
                    <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                      <i className="ri-mail-open-line text-3xl text-foreground-300"></i>
                    </div>
                    <p className="text-foreground-500 text-sm mt-4">选择左侧对话查看消息详情</p>
                  </div>
                </div>
              ) : (
                <div className="bg-background-50 border border-background-200 rounded-lg flex flex-col min-h-[500px]">
                  {/* Conversation header */}
                  <div className="px-5 py-4 border-b border-background-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-foreground-900 text-base font-bold font-heading">
                          {selectedConv.counterparty}
                        </p>
                        {selectedConv.listingType && selectedConv.listingId > 0 && (
                          <Link
                            to={`/${selectedConv.listingType}/${selectedConv.listingId}`}
                            className="text-primary-600 hover:text-primary-700 text-xs mt-0.5 inline-block cursor-pointer"
                          >
                            查看挂牌详情 <i className="ri-arrow-right-up-line text-xs"></i>
                          </Link>
                        )}
                      </div>
                      {selectedConv.listingType && (
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          selectedConv.listingType === 'supply'
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-accent-100 text-accent-700'
                        }`}>
                          {selectedConv.listingType === 'supply' ? '供给方' : '需求方'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 px-5 py-4 space-y-4 overflow-y-auto max-h-[400px]">
                    {convMessages.length === 0 ? (
                      <p className="text-foreground-400 text-sm text-center py-8">暂无消息记录</p>
                    ) : (
                      convMessages.map((msg) => {
                        const isMine = user ? msg.senderId === user.id : false;
                        return (
                          <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] rounded-lg px-4 py-3 ${
                              isMine
                                ? 'bg-primary-500 text-background-50 rounded-br-sm'
                                : 'bg-background-100 text-foreground-800 rounded-bl-sm'
                            }`}>
                              {!isMine && (
                                <p className="text-foreground-400 text-xs mb-1">{msg.senderName}</p>
                              )}
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                              <p className={`text-xs mt-1.5 ${isMine ? 'text-background-50/70' : 'text-foreground-400'}`}>
                                {formatTime(msg.createdAt)}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Reply input */}
                  <div className="px-5 py-4 border-t border-background-200">
                    <div className="flex items-end gap-3">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        maxLength={500}
                        placeholder={`回复 ${selectedConv.counterparty}…`}
                        className="flex-1 px-4 py-2.5 text-sm text-foreground-900 bg-background-100 rounded-lg border border-background-200 resize-none focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 placeholder:text-foreground-300 h-10 max-h-24"
                      />
                      <button
                        onClick={handleReply}
                        disabled={!replyText.trim() || replyText.length > 500 || sending}
                        className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                      >
                        {sending ? '发送中' : '发送'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Link to profile message center */}
          <div className="mt-6 text-center">
            <Link
              to="/profile"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-settings-3-line"></i>
              前往个人中心查看系统通知和询价消息
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}